import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { StackedBarComponent, StackedBarSegment } from '../../components/stacked-bar/stacked-bar.component';
import { SectionTitleComponent } from '../../../components/section-title/section-title.component';
import { ProjectProgress } from '../../models/dashboard.model';

const PROJECT_COLOR_VARS = [
  '--to-blue-dot',
  '--to-purple-dot',
  '--to-green-dot',
  '--to-warn-border',
  '--to-cyan-dot',
] as const;

interface LegendItem {
  key: string;
  colorVar: string;
  count: number;
}

interface ProjectRow {
  projectId: string;
  name: string;
  colorVar: string;
  passRate: number;
  total: number;
  requirementsLink: unknown[];
  segments: StackedBarSegment[];
  barAriaParams: Record<string, string | number>;
  legend: LegendItem[];
}

@Component({
  selector: 'app-test-progress',
  imports: [RouterLink, TranslatePipe, MatIcon, StackedBarComponent, SectionTitleComponent],
  templateUrl: './test-progress.component.html',
  styleUrl: './test-progress.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestProgressComponent {
  projects = input.required<ProjectProgress[]>();

  protected readonly headingId = 'test-progress-heading';

  protected readonly rows = computed<ProjectRow[]>(() =>
    this.projects().map((project, index) => {
      const evaluated = project.passed + project.failed;
      return {
        projectId: project.projectId,
        name: project.name,
        colorVar: PROJECT_COLOR_VARS[index % PROJECT_COLOR_VARS.length],
        passRate: evaluated > 0 ? Math.round((project.passed / evaluated) * 100) : 0,
        total: project.total,
        requirementsLink: ['/projects', project.projectId, 'requirements'],
        segments: [
          { label: 'passed', value: project.passed, colorVar: '--to-chart-passed' },
          { label: 'failed', value: project.failed, colorVar: '--to-chart-failed' },
          { label: 'blocked', value: project.blocked, colorVar: '--to-warn-border' },
          { label: 'running', value: project.running, colorVar: '--to-chart-running' },
          { label: 'skipped', value: project.skipped, colorVar: '--to-chart-skipped' },
        ],
        barAriaParams: {
          name: project.name,
          passed: project.passed,
          failed: project.failed,
          blocked: project.blocked,
          running: project.running,
          skipped: project.skipped,
        },
        legend: [
          { key: 'passed', colorVar: '--to-chart-passed', count: project.passed },
          { key: 'failed', colorVar: '--to-chart-failed', count: project.failed },
          { key: 'blocked', colorVar: '--to-warn-border', count: project.blocked },
          { key: 'running', colorVar: '--to-chart-running', count: project.running },
          { key: 'skipped', colorVar: '--to-chart-skipped', count: project.skipped },
        ],
      };
    }),
  );
}
